import { AlertTriangle, ArrowLeft, Check, HelpCircle, Mail } from 'lucide-react';
import type React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useSkipPolicyUpload } from '@/features/Policy/hooks/useSkipPolicyUpload';
import { isFeatureActive } from '@/lib/featureFlags';
import { SupportedProductType } from '@/types/api';
import type { UnreadableReason } from '@/types/app';
import { FeatureFlag } from '@/types/app';

interface UploadItem {
	title: string;
	description: string;
	tooltip?: string;
}

/**
 * Known reasons the page has dedicated copy for in the generic reason-box.
 * The MKB Verzuim-Ontzorgverzekering bundle (formerly its own
 * `SME_ABSENCE_RELIEF` reason) is now flagged via `policy_warnings` and
 * handled separately by the bundled banner. Anything else (or missing
 * data, e.g. after a page refresh where appData isn't rehydrated) falls
 * through to the generic headline/lead block — that's also what older
 * FE bundles will see if a future backend adds a new reason value the
 * FE doesn't yet recognise.
 *
 * `SME_ABSENCE_RELIEF` is intentionally still listed: historical DB rows
 * may still surface it, and the bundled banner takes precedence over the
 * reason-box anyway when the warning is present (which it will be for
 * those rows).
 */
const KNOWN_REASONS: readonly UnreadableReason[] = [
	'NO_POLICY_DETECTED',
	'WRONG_PRODUCT_TYPE',
	'INCOMPLETE_DOCUMENT',
	'PROTECTED_PDF',
	'TECHNICAL_FAILURE',
	'KVK_UNAVAILABLE',
	'SME_ABSENCE_RELIEF'
] as const;

function isKnownReason(value: unknown): value is UnreadableReason {
	return typeof value === 'string' && (KNOWN_REASONS as readonly string[]).includes(value);
}

const PolicyDocumentIllustration = () => (
	<div className="bg-white rounded-lg shadow-lg pt-7 pb-8 px-6 border border-gray-200 w-56 mx-auto">
		{/* Document header */}
		<div className="border-b border-gray-200 pb-3 mb-3">
			<div className="flex items-center gap-2 mb-1">
				<div className="w-6 h-6 bg-primary/20 rounded" />
				<div className="h-2.5 bg-gray-300 rounded w-28" />
			</div>
			<div className="h-2 bg-gray-200 rounded w-36 mt-1" />
		</div>

		{/* Premie highlight */}
		<div className="bg-primary/10 border-l-4 border-primary p-2 rounded-r-lg mb-2">
			<div className="flex items-center justify-between">
				<div className="h-2.5 bg-primary/40 rounded w-5" />
				<div className="h-2 bg-primary/30 rounded w-30 mr-2" />
			</div>
		</div>

		{/* Dekking highlight */}
		<div className="bg-primary/10 border-l-4 border-primary p-2 rounded-r-lg mb-2">
			<div className="flex items-center justify-between">
				<div className="h-2.5 bg-primary/40 rounded w-5" />
				<div className="h-2 bg-primary/30 rounded w-30 mr-2" />
			</div>
		</div>

		{/* Eigen risico highlight */}
		<div className="bg-primary/10 border-l-4 border-primary p-2 rounded-r-lg mb-2">
			<div className="flex items-center justify-between">
				<div className="h-2.5 bg-primary/40 rounded w-5" />
				<div className="h-2 bg-primary/30 rounded w-30 mr-2" />
			</div>
		</div>

		{/* Werkgeverslasten highlight */}
		<div className="bg-primary/10 border-l-4 border-primary p-2 rounded-r-lg mb-2">
			<div className="flex items-center justify-between">
				<div className="h-2.5 bg-primary/40 rounded w-5" />
				<div className="h-2 bg-primary/30 rounded w-30 mr-2" />
			</div>
		</div>

		{/* Footer lines */}
		<div className="mt-3 pt-3 border-t border-gray-200">
			<div className="flex gap-2">
				<div className="h-2 bg-gray-100 rounded flex-1" />
				<div className="h-2 bg-gray-100 rounded w-16" />
			</div>
		</div>
	</div>
);

interface BundledArboBannerProps {
	hasArbodienstverlening: boolean;
	isCaptchaReady: boolean;
	isProcessing: boolean;
	onContinue: () => void;
}

/**
 * Drop-in replacement for the generic reason-box, shown only when the
 * backend flagged the upload as a bundled sick-leave + arbodienstverlening
 * package (`unreadable_reason === 'SME_ABSENCE_RELIEF'`). The rest of the
 * page (what-to-upload, illustration, bottom CTAs) renders unchanged —
 * this banner just swaps the top-of-page call-out for bundle-specific copy
 * and wires an inline CTA that drops the customer into the existing
 * no-policy flow for a stand-alone SLST quote.
 *
 * Captcha state and the actual skip handler are owned by the parent so the
 * bottom-page "Ga verder zonder polis" fallback CTA shares the same hook
 * instance — clicking either button disables both while the request is
 * in-flight, and the captcha is set up exactly once.
 */
const BundledArboBanner = ({
	hasArbodienstverlening,
	isCaptchaReady,
	isProcessing,
	onContinue
}: BundledArboBannerProps): React.JSX.Element => {
	const { t } = useTranslation();

	const explanationKey = hasArbodienstverlening
		? 'policy.unreadable.bundledArbo.explanationWithArbo'
		: 'policy.unreadable.bundledArbo.explanationNoArbo';

	return (
		<div
			className="mb-9 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-5 xl:w-4/5"
			data-testid="bundled-arbo-unreadable"
		>
			<AlertTriangle className="w-5 h-5 text-amber-600 mt-1 shrink-0" />
			<div className="flex-1">
				<p className="font-semibold text-foreground text-lg">
					{t('policy.unreadable.bundledArbo.title')}
				</p>
				<p className="mt-2 text-foreground/70 leading-relaxed">
					{t('policy.unreadable.bundledArbo.lead')}
				</p>
				<p className="mt-4 font-semibold text-foreground">
					{t('policy.unreadable.bundledArbo.howToContinueTitle')}
				</p>
				<p className="mt-1 text-foreground/70 leading-relaxed">
					<Trans i18nKey={explanationKey} components={[<strong key="0" />]} />
				</p>
				<Button
					type="button"
					variant="default"
					size="lg"
					className="mt-5 px-6 py-3 text-base font-medium"
					onClick={onContinue}
					disabled={!isCaptchaReady || isProcessing}
					loading={isProcessing}
					loadingText={t('policy.unreadable.bundledArbo.ctaContinueLoading')}
					data-testid="bundled-arbo-continue-button"
				>
					{t('policy.unreadable.bundledArbo.ctaContinue')}
				</Button>
			</div>
		</div>
	);
};

const PolicyUnreadablePage = (): React.JSX.Element => {
	const { t } = useTranslation();
	const { appData, config } = useAppContext();
	// One hook instance, shared by the bundled-arbo banner CTA and the
	// bottom "Ga verder zonder polis" fallback CTA. This guarantees:
	//   1. Captcha is set up once for both buttons.
	//   2. Clicking either disables both while the no-policy submit is in
	//      flight (so the user can't double-fire the request).
	// Importantly it also makes the bottom CTA actually run the no-policy
	// flow rather than being a `<Link to="/">`. That `Link` was the
	// "fallback" Maarten flagged: when the FE doesn't recognise the reason
	// the backend sent (e.g. an older bundle on stage missing a new reason
	// value, or a future addition), the bundled banner doesn't render and
	// the bottom CTA was the only path forward — except it just dumped
	// users back on the upload form instead of triggering the flow.
	const { isCaptchaReady, isProcessing, skip } = useSkipPolicyUpload();

	const items = t('policy.unreadable.whatToUpload.items', {
		returnObjects: true
	}) as UploadItem[];

	// Reason is populated by the backend once the workflow bails out. On a
	// fresh page load / hard refresh the app context isn't rehydrated, so the
	// reason box is simply omitted and the user still sees the generic page.
	const reason = isKnownReason(appData?.unreadable_reason) ? appData.unreadable_reason : null;
	const allowNoPolicyFlow = isFeatureActive(config, FeatureFlag.ALLOW_NO_POLICY_FLOW);

	// Bundled sick-leave + arbodienstverlening package (e.g. MKB Verzuim-
	// Ontzorgverzekering). Detection moved from the legacy
	// `unreadable_reason === 'SME_ABSENCE_RELIEF'` signal to the new
	// non-blocking `policy_warnings` list, so the banner also fires when
	// the funnel bailed out for a downstream reason (NO_POLICY_DETECTED /
	// WRONG_PRODUCT_TYPE) but the LLM still recognised the bundle. Keeps
	// the historical `unreadable_reason` value working for any DB rows
	// that pre-date the warning system.
	const isBundledArbo =
		(appData?.policy_warnings ?? []).includes('SME_ABSENCE_RELIEF_BUNDLE') ||
		appData?.unreadable_reason === 'SME_ABSENCE_RELIEF';

	return (
		<section className="flex grow items-center">
			<div className="container mx-auto px-4 pt-10 pb-32">
				<div className="lg:w-9/12 mx-auto">
					{/* Header — unchanged generic copy */}
					<div className=" mb-9">
						<h1 className="text-4xl font-bold mb-6">{t('policy.unreadable.title')}</h1>
						<p className="text-xl leading-relaxed text-foreground/60">
							{t('policy.unreadable.lead')}
						</p>
					</div>

					{/*
					 * Top-of-page call-out slot. Bundled Verzuim-Ontzorg wins
					 * when present (non-quotable product, needs its own CTA);
					 * otherwise the classic reason-box renders for known
					 * backend-reported unreadable reasons.
					 */}
					{isBundledArbo ? (
						<BundledArboBanner
							hasArbodienstverlening={Boolean(
								Array.isArray(appData?.relevant_product_types) &&
									appData.relevant_product_types.includes(
										SupportedProductType.OCCUPATIONAL_HEALTH_SERVICE
									)
							)}
							isCaptchaReady={isCaptchaReady}
							isProcessing={isProcessing}
							onContinue={skip}
						/>
					) : (
						reason && (
							<div
								className="mb-9 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 xl:w-4/5"
								data-testid="unreadable-reason-box"
								data-reason={reason}
							>
								<AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
								<div>
									<p className="font-semibold text-foreground">
										{t(`policy.unreadable.reasons.${reason}.label`)}
									</p>
									<p className="mt-1 text-foreground/70 leading-relaxed whitespace-pre-line">
										{t(`policy.unreadable.reasons.${reason}.description`)}
									</p>
								</div>
							</div>
						)
					)}

					{/* What to upload section */}
					<div className="mb-10">
						<h2 className="text-xl font-semibold mb-2">
							{t('policy.unreadable.whatToUpload.title')}
						</h2>
						<p className="text-lg text-foreground/60 leading-loose xl:w-4/5 mb-9">
							<Trans
								i18nKey="policy.unreadable.whatToUpload.description"
								components={[<strong key="0" />]}
							/>
						</p>
						<div className="flex flex-col lg:flex-row gap-9 mb-10 pt-1 pb-2">
							<div className="hidden lg:block shrink-0">
								<PolicyDocumentIllustration />
							</div>
							<TooltipProvider>
								<ul className="space-y-4 text-lg flex-1 mt-3">
									{items.map((item) => (
										<li key={item.title} className="flex items-start gap-3">
											<Check className="w-5 h-5 text-primary mt-1 shrink-0" strokeWidth={3} />
											<div>
												<div className="flex items-center gap-2">
													<span className="font-semibold">{item.title}</span>
													<Tooltip>
														<TooltipTrigger>
															<HelpCircle className="w-3.5 h-3.5 text-foreground/40" />
														</TooltipTrigger>
														<TooltipContent>
															<p>{item.tooltip || item.description}</p>
														</TooltipContent>
													</Tooltip>
												</div>
												<div className="text-foreground/60">{item.description}</div>
											</div>
										</li>
									))}
								</ul>
							</TooltipProvider>
						</div>

						<p className="mt-8 text-lg">
							<Trans
								i18nKey="policy.unreadable.whatToUpload.additionalInfo"
								components={[
									<strong key="0" className="font-semibold text-foreground/70" />,
									<strong key="1" className="font-semibold text-foreground/70" />
								]}
							/>
						</p>
						<p className="mt-2 text-lg">{t('policy.unreadable.whatToUpload.outro')}</p>
						<p className="mt-7 text-lg mb-12">{t('policy.unreadable.whatToUpload.outroHelp')}</p>
					</div>

					<div className="flex flex-col sm:flex-row gap-3 justify-start">
						<Button
							variant="default"
							size="lg"
							className="pl-3 pr-6 py-3 text-base font-medium"
							asChild
						>
							<Link to="/">
								<ArrowLeft className="w-4 h-4 ml-2" />
								Probeer het opnieuw
							</Link>
						</Button>
						{/*
						 * Escape hatch into the no-PDF flow (scenario A).
						 * Users whose PDF keeps failing can complete the funnel
						 * by answering a few questions instead. We invoke the
						 * no-policy submit directly here (captcha → POST
						 * /policy-upload-forms/submit → redirect) so the user
						 * lands straight on the modal-driven entry — sending
						 * them back to `/` first leaves them hunting for the
						 * second skip button on the upload form.
						 */}
						{allowNoPolicyFlow && (
							<Button
								type="button"
								variant="outline"
								size="lg"
								className="px-5 py-3 text-base font-medium"
								onClick={skip}
								disabled={!isCaptchaReady || isProcessing}
								loading={isProcessing}
								loadingText={t('policy.unreadable.skipUploadCtaLoading')}
								data-testid="unreadable-skip-upload-cta"
							>
								{t('policy.unreadable.skipUploadCta')}
							</Button>
						)}
						<Button
							type="button"
							variant="outline"
							size="lg"
							className="px-5 py-3 text-base font-medium"
							onClick={() => {
								window.dispatchEvent(new CustomEvent('openContactModal'));
							}}
							icon={<Mail className="w-4 h-4" />}
						>
							{t('common.contactUs')}
						</Button>
					</div>
				</div>
			</div>
		</section>
	);
};

export default PolicyUnreadablePage;
